import { createAsyncThunk } from "@src/store/createAsyncThunk";
import { setTargetUrl } from "@src/store/slices/experienceBuilder/common/thunks/setTargetUrl";
import {
  clearDemoUrl,
  writeDemoUrl,
} from "@src/store/slices/experienceBuilder/common/utils/demoUrlStorage";
import { isValidDemoUrl } from "@src/store/slices/experienceBuilder/common/utils/isValidDemoUrl";
import { resolveFullUrl } from "@src/store/slices/experienceBuilder/common/utils/resolveFullUrl";
import { experienceBuilderSlice } from "@src/store/slices/experienceBuilder/slice";
import { getOrganization } from "@src/store/slices/organization";

// Concrete-slice action (never the `ExperienceBuilderAction` aggregator — that
// causes circular init in redux code).
const { _setDemoUrl } = experienceBuilderSlice.actions;

/**
 * Set (or clear) the demo URL and navigate the builder to it.
 *
 * Write-through: redux holds the live value; sessionStorage is the persistence
 * edge (org-scoped, so it's only rehydrated for the org that set it). An
 * empty/blank `url` clears the demo URL and resets the preview to the org's
 * default URL.
 *
 * Rejects anything that isn't an http(s) URL. Demo mode is derived state —
 * `isAuthenticated` is `!demoUrl` — so committing an unparseable value would
 * put the whole builder into demo mode with no origin for the Navigator or the
 * Shopify tools to use. The dialog gates its Apply button on the same
 * predicate, but this thunk is exported and cannot rely on that.
 */
export const setDemoUrl = createAsyncThunk<void, string>(
  "experienceBuilder/setDemoUrl",
  (url, { dispatch, getState }) => {
    const org = getOrganization(getState());
    const trimmed = url.trim();

    if (!trimmed) {
      clearDemoUrl();
      dispatch(_setDemoUrl(null));
      void dispatch(setTargetUrl(resolveFullUrl("/", org)));
      return;
    }

    if (!isValidDemoUrl(trimmed)) {
      throw new Error(
        `Not a valid demo URL: "${trimmed}". Expected an http(s) URL.`
      );
    }

    if (org?.id) {
      writeDemoUrl(org.id, trimmed);
    }
    dispatch(_setDemoUrl(trimmed));
    void dispatch(setTargetUrl(trimmed));
  }
);
